/**
 * services/permissions-check.js — boot-time CRM permission audit.
 *
 * #165. Before the chat list renders, we ask the proxy whether the
 * current session's CRM role has the permissions the inbox needs. The
 * proxy's existing hard gate (crm.live_chat.ALL) surfaces missing access
 * as a raw 403, which looks like "the inbox is broken" rather than "your
 * admin forgot to grant you Live Chat". This service runs once at boot
 * and returns structured data the PermissionErrorScreen renders into a
 * human-readable checklist pointing back at CRM → Team → Roles.
 *
 * Contract with the proxy's /permissions-check route:
 *   {
 *     ok:                 boolean,   // false when a REQUIRED proxy-enforced
 *                                    // perm is missing (i.e. we'd 403 on
 *                                    // every other route)
 *     ownerBypass:        boolean,   // true when session.user_type === 'owner'
 *     missing_required:   [{ label, path, breaks }],
 *     runtime_hints:      [{ label, path, breaks }],   // CRM-enforced —
 *                                                     // admin should verify
 *     missing_recommended:[{ label, path, breaks }],
 *   }
 *
 * Owner sessions always come back with `ok: true, ownerBypass: true` —
 * they can't lock themselves out of their own inbox no matter what the
 * permissions array looks like (the proxy bypasses the check for them).
 */

import { atom } from 'nanostores';
import * as api from './api.js';
import { navigate } from '../stores/ui.js';
import { SCREENS } from '../shared/constants.js';

/**
 * Reactive state the UI binds to. Shape:
 *   {
 *     checked:             boolean,  // true after first attempt (ok or not)
 *     ok:                  boolean,  // false = show error screen
 *     ownerBypass:         boolean,
 *     missing_required:    Array,
 *     runtime_hints:       Array,
 *     missing_recommended: Array,
 *     error:               string|null,  // network / 5xx failure message
 *   }
 *
 * Before the first check runs the store is `{ checked: false, ok: true, ... }`
 * so nothing renders the error screen by default — only after `checked`
 * flips to true AND `ok` is false do we navigate away from chat-list.
 */
export const $permissions = atom({
  checked:             false,
  ok:                  true,
  ownerBypass:         false,
  missing_required:    [],
  runtime_hints:       [],
  missing_recommended: [],
  error:               null,
});

/**
 * Run the permission audit against the proxy. Safe to call repeatedly —
 * the store is overwritten each call so the Retry button on the error
 * screen just triggers another run.
 *
 * Returns the freshly-written store value for callers that want to act
 * on the outcome synchronously (e.g. app.js boot flow deciding whether
 * to navigate to PERMISSION_ERROR or CHAT_LIST).
 */
export async function checkPermissions() {
  try {
    const res = await api.fetchPermissionsCheck();
    // The proxy wraps every response in { success, data }. The actual
    // validation payload is in `data.data`.
    const payload = res?.data || {};
    const value = {
      checked:             true,
      ok:                  payload.ok !== false,
      ownerBypass:         !!payload.ownerBypass,
      missing_required:    Array.isArray(payload.missing_required)    ? payload.missing_required    : [],
      runtime_hints:       Array.isArray(payload.runtime_hints)       ? payload.runtime_hints       : [],
      missing_recommended: Array.isArray(payload.missing_recommended) ? payload.missing_recommended : [],
      error:               null,
    };
    $permissions.set(value);
    return value;
  } catch (e) {
    // Network error, 5xx, or auth failure. Treat as non-blocking — the
    // normal 401 path already clears the session and kicks to login;
    // anything else we log and let the rest of boot continue, because
    // failing the inbox because /permissions-check is down would be
    // worse than any permissions mismatch the endpoint was trying to
    // catch.
    console.warn('[permissions-check] audit failed:', e?.message);
    const value = {
      checked:             true,
      ok:                  true,       // fail-open: don't brick the inbox
      ownerBypass:         false,
      missing_required:    [],
      runtime_hints:       [],
      missing_recommended: [],
      error:               e?.message || 'Permission check failed',
    };
    $permissions.set(value);
    return value;
  }
}

/**
 * Post-login navigation helper. Runs the permission audit, then either
 * drops the user into the chat list (the common case) or into the
 * PermissionErrorScreen when the audit failed. Called from all three
 * session-entry points (magic link, /refresh rehydrate, OTP login) so
 * we have exactly one place that decides "which screen does an
 * authenticated user see after their session lands".
 */
export async function navigateAfterLogin() {
  const result = await checkPermissions();
  if (result.ok) {
    navigate(SCREENS.CHAT_LIST);
  } else {
    navigate(SCREENS.PERMISSION_ERROR);
  }
  return result;
}
